import React, { useState, useEffect, useMemo, memo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Crown, Star, Medal } from 'lucide-react';
import styles from './LeaderboardDisplay.module.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo URL
const LOGO_URL = '/AIC-Logo.png';

const categories = [
  { key: 'All', label: 'All Participants', color: '#08513F' },
  { key: 'Muslim', label: 'Muslim Boys', color: '#2A9D8F' },
  { key: 'Muslimah', label: 'Muslimah Girls', color: '#E76F51' }
];

const getBackgroundClass = (key) => {
  switch (key) {
    case 'All': return styles.bgAll;
    case 'Muslim': return styles.bgMuslim;
    case 'Muslimah': return styles.bgMuslimah;
    default: return styles.bgAll;
  }
};

// Memoized Podium Participant Component
const PodiumParticipant = memo(({ child, actualRank, isFirst, isSecond }) => {
  const profileImage = child.profile_image;
  
  const podiumHeight = isFirst ? 'h-20 sm:h-24 lg:h-32' : isSecond ? 'h-16 sm:h-20 lg:h-28' : 'h-14 sm:h-16 lg:h-24';
  const avatarSize = isFirst ? 'h-14 w-14 sm:h-16 sm:w-16 lg:h-20 lg:w-20' : 'h-12 w-12 sm:h-14 sm:w-14 lg:h-16 lg:w-16';
  const fontSize = isFirst ? 'text-base sm:text-lg lg:text-xl' : 'text-sm sm:text-base lg:text-lg';
  const pointsSize = isFirst ? 'text-lg sm:text-xl lg:text-2xl' : 'text-base sm:text-lg lg:text-xl';
  const podiumColor = isFirst 
    ? 'bg-gradient-to-t from-[#D4AF37] to-[#F4D03F]' 
    : isSecond 
      ? 'bg-gradient-to-t from-[#C0C0C0] to-[#E8E8E8]'
      : 'bg-gradient-to-t from-[#CD7F32] to-[#E8A84C]';
  
  const glowClass = isFirst ? 'animate-podium-glow-gold' : isSecond ? 'animate-podium-glow-silver' : 'animate-podium-glow-bronze';
  
  return (
    <div 
      className="flex flex-col items-center flex-shrink-0 animate-slide-up"
    >
      {/* Crown for 1st place */}
      {isFirst && (
        <Crown className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-[#D4AF37] mb-1 animate-bounce" />
      )}
      
      {/* Avatar */}
      <Avatar 
        className={`${avatarSize} shadow-lg mb-1 sm:mb-2 border-2 ${isFirst ? 'border-[#D4AF37]' : isSecond ? 'border-[#C0C0C0]' : 'border-[#CD7F32]'} ${glowClass}`}
      >
        <AvatarImage src={profileImage} loading="eager" />
        <AvatarFallback className={`${fontSize} font-bold ${
          child.gender === 'Muslim' 
            ? 'bg-[#08513F] text-white' 
            : 'bg-[#E76F51] text-white'
        }`}>
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            `${child.first_name?.charAt(0)}${child.last_name?.charAt(0)}`
          )}
        </AvatarFallback>
      </Avatar>
      
      {/* Name */}
      <p className={`text-white font-bold font-outfit ${fontSize} text-center mb-1 px-1 truncate max-w-24`}>
        {child.first_name} {child.last_name?.charAt(0)}.
      </p>
      
      {/* Points */}
      <div className="flex items-center gap-1 text-[#D4AF37] mb-2 sm:mb-3">
        <Star className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
        <span className={`font-bold font-outfit ${pointsSize}`}>
          {child.total_points}
        </span>
      </div>
      
      {/* Podium */}
      <div className={`w-14 sm:w-16 lg:w-20 ${podiumHeight} ${podiumColor} rounded-t-lg flex items-center justify-center shadow-lg relative overflow-hidden ${
        glowClass
      }`}>        
        <span className="text-base sm:text-lg lg:text-xl font-black text-white/90 font-outfit relative z-10">
          {actualRank}
        </span>
      </div>
    </div>
  );
});

PodiumParticipant.displayName = 'PodiumParticipant';

// Memoized Leaderboard Item Component
const LeaderboardItem = memo(({ child }) => {
  const profileImage = child.profile_image;
  
  return (
    <div 
      className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg animate-slide-up"
      style={{ 
        backgroundColor: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(10px)'
      }}
    >
      {/* Rank */}
      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
        <span className="text-sm sm:text-base font-bold text-white font-outfit">
          {child.rank}
        </span>
      </div>
      
      {/* Avatar */}
      <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border-2 sm:border-3 border-white/30 flex-shrink-0">
        <AvatarImage src={profileImage} loading="lazy" />
        <AvatarFallback className={`text-sm sm:text-base font-bold ${
          child.gender === 'Muslim' 
            ? 'bg-[#08513F] text-white' 
            : 'bg-[#E76F51] text-white'
        }`}>
          {isLoading ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            `${child.first_name?.charAt(0)}${child.last_name?.charAt(0)}`
          )}
        </AvatarFallback>
      </Avatar>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm sm:text-base font-bold text-white font-outfit truncate">
          {child.first_name} {child.last_name?.charAt(0)}.
        </p>
      </div>
      
      {/* Points */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Star className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]" />
        <span className="text-sm sm:text-base font-bold text-white font-outfit">
          {child.total_points}
        </span>
      </div>
    </div>
  );
});

LeaderboardItem.displayName = 'LeaderboardItem';

export default function LeaderboardDisplay() {
  const [categoryIndex, setCategoryIndex] = useState(0);
  const restRef = useRef();

  const currentCategory = categories[categoryIndex];

  // Fetch leaderboard data with TanStack Query
  const { data: allLeaderboard = [], isLoading: loading, error } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/leaderboard`);
      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard');
      }
      return response.json();
    },
    initialData: () => {
      try {
        const cached = localStorage.getItem('leaderboard-cache');
        return cached ? JSON.parse(cached) : undefined;
      } catch {
        return undefined;
      }
    },
    refetchInterval: 10000, // Refetch every 10 seconds
    refetchIntervalInBackground: true,
    staleTime: 10000, // Cache for 10 seconds
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  // Custom localStorage caching with size optimization
  useEffect(() => {
    if (allLeaderboard.length > 0) {
      try {
        // Create a lightweight version for caching (exclude large profile_image URLs)
        const lightweightData = allLeaderboard.map(child => ({
          child_id: child.child_id,
          first_name: child.first_name,
          last_name: child.last_name,
          gender: child.gender,
          total_points: child.total_points,
          current_streak: child.current_streak,
          longest_streak: child.longest_streak,
          // Only keep profile_image if it's a reasonable size
          profile_image: child.profile_image && child.profile_image.length < 200 
            ? child.profile_image 
            : null
        }));
        
        localStorage.setItem('leaderboard-cache', JSON.stringify(lightweightData));
      } catch (error) {
        if (error.name === 'QuotaExceededError') {
          console.warn('localStorage quota exceeded, trying to clear old cache...');
          // Try to clear old cache and save minimal data
          try {
            localStorage.removeItem('leaderboard-cache');
            // Save only essential data without images
            const minimalData = allLeaderboard.map(child => ({
              child_id: child.child_id,
              first_name: child.first_name,
              last_name: child.last_name,
              gender: child.gender,
              total_points: child.total_points
            }));
            localStorage.setItem('leaderboard-cache', JSON.stringify(minimalData));
          } catch (fallbackError) {
            console.warn('Even minimal cache failed, disabling localStorage cache');
            // Optionally disable future caching attempts
          }
        } else {
          console.warn('Failed to save leaderboard to localStorage:', error);
        }
      }
    }
  }, [allLeaderboard]);

  // Filter and rank leaderboard based on current category
  const leaderboard = useMemo(() => {
    if (allLeaderboard.length === 0) return [];

    let filtered = allLeaderboard;
    if (currentCategory.key !== 'All') {
      filtered = allLeaderboard.filter(child => child.gender === currentCategory.key);
    }

    // Re-rank the filtered list
    return filtered.map((child, index) => ({
      ...child,
      rank: index + 1
    }));
  }, [allLeaderboard, currentCategory.key]);

  // Calculate leaderboard slices
  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3, 20);

  // Reorder for podium: [2nd, 1st, 3rd]
  const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;

  // Auto-rotate categories every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCategoryIndex((prev) => (prev + 1) % categories.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={styles.container}>
      <div 
        className={`h-screen w-full overflow-hidden ${getBackgroundClass(currentCategory.key)}`}
        style={{ 
          transition: 'background 0.5s ease-in-out',
          height: '100vh'
        }}
      >
      {/* Header */}
      <div className="pt-2 pb-1 px-2 text-center flex-shrink-0">
        <div className="flex items-center justify-center gap-2 sm:gap-3 mb-1">
          <img 
            src={LOGO_URL} 
            alt="Alnoor Islamic Center" 
            className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 object-contain"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white font-outfit tracking-tight">
              AIC Super Muslims Challenge
            </h1>
          </div>
        </div>
        
        {/* Category Title */}
        <div className="mt-2">
          <div 
            className="inline-block px-3 py-1 sm:px-4 sm:py-2 lg:px-6 lg:py-3 rounded-full text-white font-bold text-sm sm:text-base lg:text-lg font-outfit shadow-2xl"
            style={{ 
              backgroundColor: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Crown className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 inline-block mr-1 sm:mr-2 text-[#D4AF37]" />
            {currentCategory.label}
          </div>
        </div>

        {/* Category Indicators */}
        <div className="flex justify-center gap-1 sm:gap-2 mt-2">
          {categories.map((cat, index) => (
            <div 
              key={cat.key}
              className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                index === categoryIndex 
                  ? 'bg-[#D4AF37] scale-125' 
                  : 'bg-white/30'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Leaderboard Content */}
      <div className="px-1 sm:px-2 lg:px-4 pb-4 h-full flex flex-col">
        <div className="flex-1 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48 sm:h-64 lg:h-96 flex-1">
            <div className="space-y-4 w-full max-w-xs sm:max-w-md">
              {/* Loading skeleton for podium */}
              <div className="flex items-end justify-center gap-2 sm:gap-4 lg:gap-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className={`${styles.skeleton} w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full mb-2 sm:mb-4`} />
                    <div className={`${styles.skeleton} w-16 h-20 sm:w-20 sm:h-24 lg:w-24 lg:h-32 rounded-t-2xl`} />
                  </div>
                ))}
              </div>
              {/* Loading skeleton for list */}
              <div className="space-y-2 mt-4 sm:mt-6 lg:mt-8">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className={`${styles.skeleton} h-10 sm:h-12 lg:h-16 rounded-2xl`} />
                ))}
              </div>
              <p className="text-white/70 mt-4 text-center text-sm sm:text-base">
                Loading leaderboard data... This may take a moment on first visit.
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-10 sm:py-16 lg:py-20 flex-1 flex items-center justify-center">
            <div>
              <Medal className="w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 text-white/30 mx-auto mb-4 sm:mb-6" />
              <p className="text-xl sm:text-2xl lg:text-4xl text-white/70 font-outfit">Failed to load leaderboard</p>
              <p className="text-sm sm:text-lg lg:text-2xl text-white/50 mt-2">Please try refreshing the page</p>
            </div>
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-10 sm:py-16 lg:py-20 flex-1 flex items-center justify-center">
            <div>
              <Medal className="w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 text-white/30 mx-auto mb-4 sm:mb-6" />
              <p className="text-xl sm:text-2xl lg:text-4xl text-white/70 font-outfit">No participants yet</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center">
            {/* Podium - Top 3 */}
            {top3.length > 0 && <>
              <div className="flex items-end justify-center gap-2 sm:gap-3 lg:gap-6 mb-3 sm:mb-4 lg:mb-6">
                {podiumOrder.map((child, i) => {
                  if (!child) return null;
                  
                  const actualRank = child.rank;
                  const isFirst = actualRank === 1;
                  const isSecond = actualRank === 2;
                  return (
                    <PodiumParticipant
                      key={child.child_id}
                      child={child}
                      actualRank={actualRank}
                      isFirst={isFirst}
                      isSecond={isSecond}
                    />
                  );
                  })}
              </div>

              {/* Rest of Leaderboard */}
              <div ref={restRef}>
                {rest.map((child) => (
                  <LeaderboardItem
                    key={child.child_id}
                    child={child}
                  />
                ))}
              </div>
            </>}
          </div>
        )}
        </div>
      </div>
    </div>
    </div>
  );
}
